/**
 * Serviço de análise de transcrições
 */

import {
  Transcription,
  TranscriptionMetrics,
  SpeakerStat,
  SearchResult,
  LegalTerm,
  TermOccurrence
} from '../types/transcription'
import { LEGAL_TERMS, LegalTermDefinition } from '../constants/legalTerms'

/**
 * Calcula métricas da transcrição
 */
export function calculateMetrics(transcription: Transcription): TranscriptionMetrics {
  const { transcript, duration } = transcription
  const totalDuration = parseFloat(duration) * 60 // Converter minutos para segundos

  // Contagem de palavras
  const totalWords = transcript.reduce((acc, seg) => {
    return acc + seg.text.split(/\s+/).filter(w => w.length > 0).length
  }, 0)

  // Estatísticas por speaker
  const speakerMap = new Map<string, { segments: number; duration: number; words: number }>()

  for (const segment of transcript) {
    const speaker = segment.speaker || 'Não identificado'
    const segmentDuration = parseFloat(segment.end_time) - parseFloat(segment.start_time)
    const wordCount = segment.text.split(/\s+/).filter(w => w.length > 0).length

    const current = speakerMap.get(speaker) || { segments: 0, duration: 0, words: 0 }
    speakerMap.set(speaker, {
      segments: current.segments + 1,
      duration: current.duration + segmentDuration,
      words: current.words + wordCount
    })
  }

  const speakerStats: SpeakerStat[] = Array.from(speakerMap.entries()).map(([speaker, stats]) => ({
    speaker,
    segmentCount: stats.segments,
    totalDuration: stats.duration,
    wordCount: stats.words,
    percentage: totalDuration > 0 ? (stats.duration / totalDuration) * 100 : 0
  })).sort((a, b) => b.totalDuration - a.totalDuration)

  const totalSegmentDuration = transcript.reduce((acc, seg) => {
    return acc + (parseFloat(seg.end_time) - parseFloat(seg.start_time))
  }, 0)

  return {
    totalDuration,
    totalWords,
    totalSegments: transcript.length,
    averageSegmentDuration: transcript.length > 0 ? totalSegmentDuration / transcript.length : 0,
    averageWordsPerSegment: transcript.length > 0 ? totalWords / transcript.length : 0,
    speakerCount: speakerMap.size,
    speakerStats,
    wordsPerMinute: totalDuration > 0 ? (totalWords / totalDuration) * 60 : 0
  }
}

/**
 * Busca texto na transcrição
 */
export function searchTranscript(transcription: Transcription, query: string): SearchResult[] {
  if (!query.trim()) return []

  const results: SearchResult[] = []
  const lowerQuery = query.toLowerCase()

  transcription.transcript.forEach((segment, index) => {
    const lowerText = segment.text.toLowerCase()
    let searchIndex = 0

    while (true) {
      const matchStart = lowerText.indexOf(lowerQuery, searchIndex)
      if (matchStart === -1) break

      results.push({
        segmentIndex: index,
        segment,
        matchedText: segment.text.substring(matchStart, matchStart + query.length),
        matchStart,
        matchEnd: matchStart + query.length
      })

      searchIndex = matchStart + 1
    }
  })

  return results
}

/**
 * Encontra termos jurídicos na transcrição com suas ocorrências
 */
export function findLegalTermsInTranscript(transcription: Transcription): LegalTerm[] {
  const termMap = new Map<string, { term: LegalTermDefinition; occurrences: TermOccurrence[] }>()

  transcription.transcript.forEach((segment, index) => {
    for (const termDef of LEGAL_TERMS) {
      const pattern = new RegExp(termDef.pattern.source, termDef.pattern.flags)
      let match: RegExpExecArray | null

      while ((match = pattern.exec(segment.text)) !== null) {
        const key = termDef.term.toLowerCase()
        const existing = termMap.get(key) || { term: termDef, occurrences: [] }

        existing.occurrences.push({
          segmentIndex: index,
          startTime: segment.start_time,
          text: segment.text
        })

        termMap.set(key, existing)
      }
    }
  })

  return Array.from(termMap.values())
    .map(({ term, occurrences }) => ({
      term: term.term,
      category: term.category,
      description: term.description,
      occurrences
    }))
    .sort((a, b) => b.occurrences.length - a.occurrences.length)
}

/**
 * Formata timestamp para exibição (mm:ss)
 */
export function formatTimestamp(seconds: string | number): string {
  const secs = typeof seconds === 'string' ? parseFloat(seconds) : seconds
  const mins = Math.floor(secs / 60)
  const remainingSecs = Math.floor(secs % 60)
  return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`
}

/**
 * Formata duração para exibição humana
 */
export function formatDuration(minutes: number): string {
  if (minutes < 1) {
    return `${Math.round(minutes * 60)} segundos`
  }
  if (minutes < 60) {
    const mins = Math.floor(minutes)
    const secs = Math.round((minutes - mins) * 60)
    return secs > 0 ? `${mins}min ${secs}s` : `${mins} minutos`
  }
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins > 0 ? `${hours}h ${mins}min` : `${hours} hora${hours > 1 ? 's' : ''}`
}

/**
 * Destaca termos jurídicos no texto
 */
export function highlightLegalTerms(text: string): { text: string; isHighlight: boolean; term?: LegalTermDefinition }[] {
  const parts: { text: string; isHighlight: boolean; term?: LegalTermDefinition }[] = []

  // Cria um array de todas as matches com suas posições
  const allMatches: { start: number; end: number; term: LegalTermDefinition }[] = []

  for (const termDef of LEGAL_TERMS) {
    const pattern = new RegExp(termDef.pattern.source, termDef.pattern.flags)
    let match: RegExpExecArray | null

    while ((match = pattern.exec(text)) !== null) {
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        term: termDef
      })
    }
  }

  // Ordena por posição
  allMatches.sort((a, b) => a.start - b.start)

  // Remove overlaps (mantém a primeira match)
  const filteredMatches = allMatches.filter((match, index) => {
    if (index === 0) return true
    const prev = allMatches[index - 1]
    return match.start >= prev.end
  })

  // Constrói o array de partes
  let lastEnd = 0
  for (const match of filteredMatches) {
    if (match.start > lastEnd) {
      parts.push({ text: text.substring(lastEnd, match.start), isHighlight: false })
    }
    parts.push({
      text: text.substring(match.start, match.end),
      isHighlight: true,
      term: match.term
    })
    lastEnd = match.end
  }

  if (lastEnd < text.length) {
    parts.push({ text: text.substring(lastEnd), isHighlight: false })
  }

  return parts.length > 0 ? parts : [{ text, isHighlight: false }]
}

/**
 * Extrai números de processo do texto
 */
export function extractProcessNumbers(text: string): string[] {
  // Padrão CNJ: NNNNNNN-DD.AAAA.J.TR.OOOO
  const cnjPattern = /\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/g
  const matches = text.match(cnjPattern) || []
  return [...new Set(matches)]
}

/**
 * Extrai valores monetários do texto
 */
export function extractMonetaryValues(text: string): { value: string; context: string }[] {
  const pattern = /R\$\s*[\d.,]+/gi
  const results: { value: string; context: string }[] = []

  let match: RegExpExecArray | null
  const regex = new RegExp(pattern.source, pattern.flags)

  while ((match = regex.exec(text)) !== null) {
    const start = Math.max(0, match.index - 30)
    const end = Math.min(text.length, match.index + match[0].length + 30)
    results.push({
      value: match[0],
      context: text.substring(start, end)
    })
  }

  return results
}

/**
 * Extrai menções a datas do texto
 */
export function extractDates(text: string): string[] {
  const patterns = [
    /\d{1,2}\s+de\s+\w+\s+(de\s+)?\d{4}/gi,
    /\d{1,2}\/\d{1,2}\/\d{2,4}/g,
    /\d{1,2}-\d{1,2}-\d{2,4}/g
  ]

  const results: string[] = []
  for (const pattern of patterns) {
    const matches = text.match(pattern) || []
    results.push(...matches)
  }

  return [...new Set(results)]
}
